import { Close } from '@mui/icons-material';
import { Dialog, DialogProps, DialogTitle, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useId } from 'react';

type ParticipantDialogFrameProps = Omit<DialogProps, 'title' | 'onClose'> & {
    title: string;
    closeLabel?: string;
    onClose: () => void;
    busy?: boolean;
    compact?: boolean;
};

export default function ParticipantDialogFrame({
    title, closeLabel = `Close ${title.toLowerCase()}`, onClose, busy = false,
    compact = false, children, maxWidth = 'sm', fullScreen: requestedFullScreen, sx, ...props
}: ParticipantDialogFrameProps) {
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const headingId = useId();
    const fullScreen = requestedFullScreen ?? (smallScreen && !compact);

    return (
        <Dialog
            {...props}
            fullWidth
            maxWidth={maxWidth}
            fullScreen={fullScreen}
            onClose={busy ? undefined : onClose}
            disableEscapeKeyDown={busy}
            aria-labelledby={headingId}
            transitionDuration={reducedMotion ? 0 : undefined}
            sx={[
                {
                    '& .MuiDialog-paper': {
                        backgroundImage: 'none',
                        maxHeight: fullScreen ? '100dvh' : `calc(100dvh - ${theme.spacing(8)})`,
                        ...(fullScreen && { height: '100dvh' }),
                    },
                    '& .MuiDialogContent-root': { px: 3, pt: 2, pb: 3, minHeight: 0, overflowWrap: 'anywhere', border: 0 },
                    '& .MuiInputBase-input': { scrollMarginBlock: theme.spacing(2) },
                    '& .MuiDialogActions-root': {
                        p: 3, pt: 1, gap: 1, flexWrap: 'wrap',
                        ...(fullScreen && { pb: `max(${theme.spacing(3)}, env(safe-area-inset-bottom))` }),
                        '& > :not(style) ~ :not(style)': { ml: 0 },
                        '& .MuiButton-root': { minHeight: theme.spacing(6) },
                        '& .MuiButton-contained': { flex: { xs: 1, sm: 'initial' } },
                    },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography id={headingId} component="span" variant="subtitle2" color="text.secondary" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {title}
                </Typography>
                <Tooltip title={closeLabel}>
                    <IconButton onClick={onClose} disabled={busy} aria-label={closeLabel} sx={{ minWidth: 'var(--touch-min)', minHeight: 'var(--touch-min)', flexShrink: 0 }}>
                        <Close />
                    </IconButton>
                </Tooltip>
            </DialogTitle>
            {children}
        </Dialog>
    );
}